import type {
  RuntimeClient,
  RuntimeNotificationPermission,
} from "@/clients/runtime.client";
import type { Transcript } from "@/types";

type TranscriptCompletionSnapshot = {
  transcriptId?: string;
  status?: Transcript["status"];
};

export const didTranscriptJustComplete = (
  previous: TranscriptCompletionSnapshot,
  current: TranscriptCompletionSnapshot,
): boolean =>
  Boolean(
    current.transcriptId &&
      previous.transcriptId === current.transcriptId &&
      previous.status &&
      previous.status !== "transcription_completed" &&
      current.status === "transcription_completed",
  );

export type NativeTranscriptCompletionDependencies = {
  notifications: RuntimeClient["notifications"];
  wasPermissionRequested: () => boolean;
  markPermissionRequested: () => void;
  // Explains why notifications are wanted before the one-shot OS prompt is
  // burned; resolves false when the user opts out of being asked.
  confirmPermissionRationale: () => Promise<boolean>;
  review: { request: () => Promise<boolean> };
};

export type NativeTranscriptCompletionOutcome =
  | { kind: "permissionPrompted"; permission: RuntimeNotificationPermission }
  | { kind: "permissionRationaleDeclined" }
  | { kind: "reviewRequested" }
  | { kind: "nothingToRequest" };

export type TranscriptBackAction =
  | "blockedByPaywall"
  | "dismissModal"
  | "dismissPrompt"
  | "dismissSearch"
  | "dismissExport"
  | "dismissLanguage"
  | "dismissMore"
  | "dismissBottomMenu"
  | "navigateHome";

export const resolveTranscriptBackAction = (state: {
  paywallVisible: boolean;
  modalVisible: boolean;
  promptVisible: boolean;
  searchVisible: boolean;
  exportVisible: boolean;
  languageVisible: boolean;
  moreVisible: boolean;
  bottomMenuVisible: boolean;
}): TranscriptBackAction => {
  if (state.paywallVisible) return "blockedByPaywall";
  if (state.modalVisible) return "dismissModal";
  if (state.promptVisible) return "dismissPrompt";
  if (state.searchVisible) return "dismissSearch";
  if (state.exportVisible) return "dismissExport";
  if (state.languageVisible) return "dismissLanguage";
  if (state.moreVisible) return "dismissMore";
  if (state.bottomMenuVisible) return "dismissBottomMenu";
  return "navigateHome";
};

export const handleNativeTranscriptCompletion = async (
  dependencies: NativeTranscriptCompletionDependencies,
): Promise<NativeTranscriptCompletionOutcome> => {
  // The first useful result gets one rating request. Optional notifications
  // can wait for another completion instead of stacking system prompts.
  try {
    if (await dependencies.review.request()) return { kind: "reviewRequested" };
  } catch {
    // An optional store prompt must never spoil the completed transcript.
  }

  if (!dependencies.wasPermissionRequested()) {
    try {
      const permission = await dependencies.notifications.checkPermission();
      if (permission === "prompt" || permission === "prompt-with-rationale") {
        const accepted = await dependencies.confirmPermissionRationale();
        dependencies.markPermissionRequested();
        if (!accepted) {
          return { kind: "permissionRationaleDeclined" };
        }
        const requested = await dependencies.notifications.requestPermission();
        return { kind: "permissionPrompted", permission: requested };
      }
      dependencies.markPermissionRequested();
    } catch {
      // A notification capability failure must not spoil the transcript.
    }
  }

  return { kind: "nothingToRequest" };
};
